import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import './AssignmentAssistant.css';
import AssistantInfoBubble from './AssistantInfoBubble';

const LOADING_STEPS = [
  'Searching taxonomy…',
  'Drafting questions…',
  'Building assignment draft…',
];

const SCOPE_LABELS = {
  full: 'Full assignment',
  questions: 'Questions only',
  directions: 'Directions only',
  taxonomy: 'Taxonomy only',
};

const PATHWAY_FIT_LABELS = {
  good: 'Good pathway match',
  weak: 'Weak pathway match',
  none: 'No pathway match',
};

const FRESH_REQUEST_NOTE =
  'NOTICE: Only the text you enter above is sent to the AI. No previous prompts, assignment data, or chat history is included. This is a LOW TIER AI ASSISTANT for cost & demo integration purposes only.';

function stripHtml(html) {
  if (!html) return '';
  return String(html)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function existingTaxonomyLinksOnly(taxonomy) {
  return (taxonomy || []).filter((t) => t.taxonomy_id);
}

function proposedTaxonomyLinks(taxonomy) {
  return (taxonomy || []).filter((t) => t.proposed_id && !t.taxonomy_id);
}

function taxonomyKey(t) {
  return `${t.taxonomy_id || t.proposed_id || ''}|${t.concentration || ''}|${t.parent_id || ''}`;
}

function mergeTaxonomy(existing, incoming) {
  const seen = new Set((existing || []).map(taxonomyKey));
  const merged = [...(existing || [])];
  for (const item of incoming || []) {
    const key = taxonomyKey(item);
    if (!seen.has(key)) {
      merged.push(item);
      seen.add(key);
    }
  }
  return merged;
}

function buildResourcesDirection(references) {
  if (!references?.length) return null;
  const items = references
    .map(
      (r) =>
        `<li><a href="${escapeHtml(r.url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(r.title)}</a>${r.note ? ` — ${escapeHtml(r.note)}` : ''}</li>`
    )
    .join('');
  return {
    _id: uuidv4(),
    element: 'richText',
    value: `<p><strong>Resources</strong> — verify links before use</p><ul>${items}</ul>`,
  };
}

function withDirectionIds(directions) {
  return (directions || []).map((d) => ({ ...d, _id: uuidv4() }));
}

function withQuestionIds(questions) {
  return (questions || []).map((q) => ({ ...q, _id: uuidv4() }));
}

const QUESTION_TYPE_LABEL = {
  directions: 'Directions',
  singleChoice: 'Single choice',
  multipleChoice: 'Multiple choice',
  openAnswer: 'Open answer',
  upload: 'Upload',
  video: 'Video',
};

const AssignmentAssistant = ({
  assignment,
  setAssignment,
  selectedConcentrations = [],
  pathwayRoots = [],
  taxonomyItems = [],
  scrollToAbSection,
  aiCapabilities = null,
  onTaxonomyRefresh,
}) => {
  const [open, setOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [promptHistory, setPromptHistory] = useState([]);
  const [activePrompt, setActivePrompt] = useState(null);
  const [input, setInput] = useState('');
  const [loadingStep, setLoadingStep] = useState(0);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [applying, setApplying] = useState(false);
  const [loadOptions, setLoadOptions] = useState({
    directions: true,
    questions: true,
    taxonomy: false,
    createTaxonomy: false,
    useTitle: true,
    includeResources: true,
  });

  const canUseAssistant = aiCapabilities?.UseAssistant === true;
  const canCreateTaxonomy = aiCapabilities?.CreateTaxonomy === true;
  const capabilitiesLoaded = aiCapabilities != null;

  const isLoading = activePrompt?.status === 'loading';
  const nextPromptNumber = promptHistory.length + 1;
  const displayPromptNumber = activePrompt?.number ?? nextPromptNumber;

  const taxonomyTitleById = useMemo(() => {
    const map = {};
    (taxonomyItems || []).forEach((item) => {
      map[item.id] = item.title;
    });
    return map;
  }, [taxonomyItems]);

  const pathwayTitleById = useMemo(() => {
    const map = {};
    (pathwayRoots || []).forEach((p) => {
      map[p.id] = p.title;
    });
    return map;
  }, [pathwayRoots]);

  const allProposedItems = useMemo(() => {
    const pathways = activePrompt?.result?.proposed_pathway_roots || [];
    const taxonomy = activePrompt?.result?.proposed_taxonomy || [];
    return [...pathways, ...taxonomy];
  }, [activePrompt?.result]);

  const proposedPathways = activePrompt?.result?.proposed_pathway_roots || [];
  const proposedTaxonomy = activePrompt?.result?.proposed_taxonomy || [];

  useEffect(() => {
    const draft = activePrompt?.result?.draft;
    if (!draft || activePrompt?.status !== 'done') return;

    const hasExistingTags = existingTaxonomyLinksOnly(draft.taxonomy).length > 0;
    const hasProposed = allProposedItems.length > 0;
    const scope = activePrompt.result.response_scope || 'full';
    const references = activePrompt.result.references || [];

    setLoadOptions({
      directions: (draft.initialdirections?.length || 0) > 0,
      questions: (draft.questions?.length || 0) > 0,
      taxonomy: hasExistingTags || hasProposed,
      createTaxonomy: hasProposed && canCreateTaxonomy,
      useTitle: scope === 'full' && Boolean(draft.title),
      includeResources: references.length > 0,
    });
  }, [
    activePrompt?.id,
    activePrompt?.status,
    activePrompt?.result,
    allProposedItems.length,
    canCreateTaxonomy,
  ]);

  useEffect(() => {
    if (!isLoading) return undefined;
    setLoadingStep(0);
    const interval = setInterval(() => {
      setLoadingStep((s) => (s + 1) % LOADING_STEPS.length);
    }, 2200);
    return () => clearInterval(interval);
  }, [isLoading]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const buildApiContext = useCallback(
    () => ({
      fresh_request: true,
      gradingresulttype: 'Points',
      hours: 1,
    }),
    []
  );

  const callDraftApi = useCallback(
    async (promptText) => {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Please log in to use the assignment assistant.');
      }

      const response = await axios.post(
        '/api/ai/assignment-draft',
        {
          messages: [{ role: 'user', content: promptText.trim() }],
          context: buildApiContext(),
          options: {
            include_web_references: false,
            max_questions: 12,
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      return response.data;
    },
    [buildApiContext]
  );

  const completePrompt = useCallback((actionLabel) => {
    if (!activePrompt) return;
    setPromptHistory((prev) => [
      ...prev,
      {
        id: activePrompt.id,
        number: activePrompt.number,
        text: activePrompt.text,
        action: actionLabel,
        scope: activePrompt.result?.response_scope || 'full',
      },
    ]);
    setActivePrompt(null);
    setInput('');
    setShowHistory(false);
  }, [activePrompt]);

  const generatePrompt = useCallback(
    async (promptText, number, existingId = null) => {
      const id = existingId || uuidv4();
      setError('');
      setShowHistory(false);
      setActivePrompt({
        id,
        number,
        text: promptText,
        status: 'loading',
        error: '',
        result: null,
      });

      try {
        const data = await callDraftApi(promptText);
        setActivePrompt((prev) =>
          prev && prev.id === id
            ? { ...prev, status: 'done', result: data, error: '' }
            : prev
        );
      } catch (err) {
        const detail = err.response?.data?.detail;
        const msg =
          [err.response?.data?.error, detail].filter(Boolean).join(': ') ||
          err.message ||
          'Failed to generate draft.';
        setError(msg);
        setActivePrompt((prev) =>
          prev && prev.id === id
            ? { ...prev, status: 'error', error: msg, result: null }
            : prev
        );
      }
    },
    [callDraftApi]
  );

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || isLoading || activePrompt?.status === 'done') return;
    setInput('');
    await generatePrompt(text, nextPromptNumber);
  }, [input, isLoading, activePrompt, nextPromptNumber, generatePrompt]);

  const handleRegenerate = useCallback(async () => {
    if (!activePrompt?.text || isLoading) return;
    await generatePrompt(activePrompt.text, activePrompt.number, activePrompt.id);
  }, [activePrompt, isLoading, generatePrompt]);

  const handleDiscard = useCallback(() => {
    setActivePrompt(null);
    setInput('');
    setError('');
  }, []);

  const showToast = useCallback((text) => {
    setToast(text);
  }, []);

  const createSelectedProposedTaxonomy = useCallback(
    async (pendingDraft) => {
      if (!allProposedItems.length) return null;

      const pathwayItems = pendingDraft.proposed_pathway_roots || [];
      const items = pendingDraft.proposed_taxonomy || [];
      const taxonomyLinks = [
        ...existingTaxonomyLinksOnly(pendingDraft.draft?.taxonomy),
        ...proposedTaxonomyLinks(pendingDraft.draft?.taxonomy),
      ];

      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/api/ai/create-proposed-taxonomy',
        { pathway_items: pathwayItems, items, taxonomy_links: taxonomyLinks },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const created = response.data.created || [];
      const resolvedLinks = response.data.taxonomy_links || [];
      const idMap = response.data.id_map || {};

      const newPathway = created.find((c) =>
        ['Concentration', 'Course-Subject'].includes(c.type)
      );

      if (onTaxonomyRefresh) {
        await onTaxonomyRefresh();
      }

      const resolvedDraftTaxonomy = existingTaxonomyLinksOnly(
        (pendingDraft.draft?.taxonomy || []).map((link) => {
          if (link.proposed_id && idMap[link.proposed_id]) {
            return {
              taxonomy_id: idMap[link.proposed_id],
              proposed_id: null,
              parent_id: link.parent_id || null,
              concentration:
                link.concentration ||
                (link.pathway_proposed_id && idMap[link.pathway_proposed_id]) ||
                newPathway?.id ||
                null,
            };
          }
          return link;
        })
      );

      setActivePrompt((prev) => {
        if (!prev?.result) return prev;
        return {
          ...prev,
          result: {
            ...prev.result,
            proposed_pathway_roots: [],
            proposed_taxonomy: [],
            draft: prev.result.draft
              ? {
                  ...prev.result.draft,
                  taxonomy: resolvedDraftTaxonomy,
                }
              : prev.result.draft,
          },
        };
      });

      return {
        created,
        resolvedLinks,
        resolvedDraftTaxonomy,
        newPathwayId: newPathway?.id || null,
      };
    },
    [allProposedItems.length, onTaxonomyRefresh]
  );

  const applyPathwayIfEmpty = useCallback(
    (draft) => {
      const suggested =
        draft.suggested_pathway_root_id ||
        draft.draft?.concentration ||
        null;
      if (!assignment.concentration && suggested) {
        setAssignment((prev) => ({ ...prev, concentration: suggested }));
      }
    },
    [assignment.concentration, setAssignment]
  );

  const handleLoad = useCallback(async () => {
    const pendingDraft = activePrompt?.result;
    const draft = pendingDraft?.draft;
    if (!pendingDraft || !draft) return;

    const {
      directions: loadDirections,
      questions: loadQuestions,
      taxonomy: loadTaxonomy,
      createTaxonomy: loadCreateTaxonomy,
      useTitle,
      includeResources,
    } = loadOptions;

    const canLoadAnything =
      (loadDirections && draft.initialdirections?.length > 0) ||
      (loadQuestions && draft.questions?.length > 0) ||
      (loadTaxonomy && existingTaxonomyLinksOnly(draft.taxonomy).length > 0) ||
      (loadCreateTaxonomy && allProposedItems.length > 0 && canCreateTaxonomy);

    if (!canLoadAnything) return;

    setApplying(true);
    setError('');
    const loadedParts = [];

    try {
      let createResult = null;
      if (loadCreateTaxonomy && allProposedItems.length > 0 && canCreateTaxonomy) {
        createResult = await createSelectedProposedTaxonomy(pendingDraft);
        if (createResult?.created?.length) {
          loadedParts.push('create taxonomy');
        }
      }

      const refs = pendingDraft.references;
      let taxonomyToApply = [];
      if (loadTaxonomy) {
        if (loadCreateTaxonomy && createResult) {
          taxonomyToApply =
            createResult.resolvedDraftTaxonomy?.length > 0
              ? createResult.resolvedDraftTaxonomy
              : createResult.resolvedLinks || [];
        } else {
          taxonomyToApply = existingTaxonomyLinksOnly(draft.taxonomy);
        }
      }

      setAssignment((prev) => {
        const next = { ...prev };

        if (useTitle && draft.title) {
          next.title = draft.title;
        }
        if (draft.hours != null && Number(draft.hours) > 0) {
          next.hours = draft.hours;
        }

        if (loadDirections && draft.initialdirections?.length > 0) {
          const blocks = withDirectionIds(draft.initialdirections);
          if (includeResources && refs?.length) {
            const resourcesBlock = buildResourcesDirection(refs);
            if (resourcesBlock) blocks.push(resourcesBlock);
          }
          next.initialdirections = [...(prev.initialdirections || []), ...blocks];
        }

        if (loadQuestions && draft.questions?.length > 0) {
          next.questions = [
            ...(prev.questions || []),
            ...withQuestionIds(draft.questions),
          ];
        }

        if (taxonomyToApply.length) {
          next.taxonomy = mergeTaxonomy(prev.taxonomy, taxonomyToApply);
        }

        if (!prev.concentration) {
          const suggested =
            pendingDraft.suggested_pathway_root_id ||
            draft.concentration ||
            createResult?.newPathwayId;
          if (suggested) next.concentration = suggested;
        }

        return next;
      });

      if (loadDirections && draft.initialdirections?.length > 0) {
        loadedParts.push('directions');
      }
      if (loadQuestions && draft.questions?.length > 0) {
        loadedParts.push('questions');
      }
      if (loadTaxonomy && taxonomyToApply.length > 0) {
        loadedParts.push('taxonomy');
      }

      applyPathwayIfEmpty(pendingDraft);

      const actionLabel =
        loadedParts.length > 0 ? `Load (${loadedParts.join(', ')})` : 'Load';
      completePrompt(actionLabel);
      showToast('Loaded into assignment — review and save when ready.');
      if (scrollToAbSection) scrollToAbSection('ab-section-preview');
    } catch (err) {
      const msg =
        [err.response?.data?.error, err.response?.data?.detail].filter(Boolean).join(': ') ||
        err.message ||
        'Failed to load draft.';
      setError(msg);
    } finally {
      setApplying(false);
    }
  }, [
    activePrompt,
    loadOptions,
    allProposedItems.length,
    canCreateTaxonomy,
    createSelectedProposedTaxonomy,
    setAssignment,
    applyPathwayIfEmpty,
    completePrompt,
    showToast,
    scrollToAbSection,
  ]);

  const renderActiveResult = () => {
    const pendingDraft = activePrompt?.result;
    const draft = pendingDraft?.draft;
    if (!pendingDraft || !draft) return null;

    const scope = pendingDraft.response_scope || 'full';
    const scopeLabel = SCOPE_LABELS[scope] || SCOPE_LABELS.full;
    const questionPointTotal =
      draft.questions?.reduce(
        (sum, q) => sum + (Number(q.maxPoints) || 0),
        0
      ) || 0;
    const hasExistingTaxonomyLinks =
      existingTaxonomyLinksOnly(draft.taxonomy).length > 0;
    const proposed = pendingDraft.proposed_taxonomy || [];
    const proposedPathwayRoots = pendingDraft.proposed_pathway_roots || [];
    const pathwayAssessment = pendingDraft.pathway_assessment;
    const showQuestions = draft.questions?.length > 0;
    const showDirections = draft.initialdirections?.length > 0;
    const showFullInsert = scope === 'full';
    const canLoad =
      (loadOptions.directions && showDirections) ||
      (loadOptions.questions && showQuestions) ||
      (loadOptions.taxonomy && hasExistingTaxonomyLinks) ||
      (loadOptions.createTaxonomy && allProposedItems.length > 0 && canCreateTaxonomy);

    return (
      <div className="aa-review">
        <div className="aa-review__scope">
          <span className="aa-review__scope-badge">{scopeLabel}</span>
          {pendingDraft.reasoning_summary && (
            <p className="aa-review__summary">{pendingDraft.reasoning_summary}</p>
          )}
        </div>

        {scope === 'full' && draft.title && (
          <p>
            <strong>{draft.title}</strong>
            {' · '}
            {draft.questions?.length || 0} questions
            {assignment.gradingresulttype !== 'None' &&
              ` · ${questionPointTotal || draft.maxpoints || 0} pts`}
          </p>
        )}

        {showDirections && (
          <p>
            {draft.initialdirections.length} direction block
            {draft.initialdirections.length !== 1 ? 's' : ''}:{' '}
            {stripHtml(draft.initialdirections[0]?.value).slice(0, 80)}
            …
          </p>
        )}

        {showQuestions && (
          <ul>
            {draft.questions.slice(0, 6).map((q, i) => (
              <li key={i}>
                {QUESTION_TYPE_LABEL[q.type] || q.type}
                {q.maxPoints != null ? ` (${q.maxPoints} pts)` : ''}:{' '}
                {stripHtml(q.label).slice(0, 60)}
                {stripHtml(q.label).length > 60 ? '…' : ''}
              </li>
            ))}
            {draft.questions.length > 6 && (
              <li>…and {draft.questions.length - 6} more</li>
            )}
          </ul>
        )}

        {(hasExistingTaxonomyLinks ||
          pendingDraft.taxonomy_explanations?.some((t) => t.taxonomy_id)) && (
          <p>
            Taxonomy:{' '}
            {(pendingDraft.taxonomy_explanations || existingTaxonomyLinksOnly(draft.taxonomy))
              .filter((t) => t.taxonomy_id || t.title)
              .slice(0, 4)
              .map((t) =>
                t.title || taxonomyTitleById[t.taxonomy_id] || t.taxonomy_id
              )
              .join(', ')}
          </p>
        )}

        {pathwayAssessment && (scope === 'full' || scope === 'taxonomy') && (
          <div className="aa-pathway-assessment">
            <strong>
              {PATHWAY_FIT_LABELS[pathwayAssessment.fit] || 'Pathway assessment'}
            </strong>
            {pathwayAssessment.matched_pathway_title && (
              <p>
                Closest pathway: {pathwayAssessment.matched_pathway_title}
              </p>
            )}
            {pathwayAssessment.recommendation && (
              <p>{pathwayAssessment.recommendation}</p>
            )}
          </div>
        )}

        {proposedPathwayRoots.length > 0 && (
          <div className="aa-proposed-taxonomy aa-proposed-taxonomy--pathway">
            <strong>Proposed new pathway ({proposedPathwayRoots.length})</strong>
            <ul>
              {proposedPathwayRoots.map((p) => (
                <li key={p.proposed_id}>
                  <span className="aa-proposed-taxonomy__type">{p.type}</span>{' '}
                  {p.title}
                  {p.why ? ` — ${p.why}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}

        {proposed.length > 0 && (
          <div className="aa-proposed-taxonomy">
            <strong>Proposed standards / skills / objectives ({proposed.length})</strong>
            <ul>
              {proposed.slice(0, 5).map((p) => (
                <li key={p.proposed_id}>
                  <span className="aa-proposed-taxonomy__type">{p.type}</span>{' '}
                  {p.title}
                  {p.why ? ` — ${p.why}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="aa-load">
          <div className="aa-load__options">
            {showDirections && (
              <label className="aa-checkbox">
                <input
                  type="checkbox"
                  checked={loadOptions.directions}
                  onChange={(e) =>
                    setLoadOptions((o) => ({ ...o, directions: e.target.checked }))
                  }
                  disabled={applying}
                />
                Directions
              </label>
            )}
            {showQuestions && (
              <label className="aa-checkbox">
                <input
                  type="checkbox"
                  checked={loadOptions.questions}
                  onChange={(e) =>
                    setLoadOptions((o) => ({ ...o, questions: e.target.checked }))
                  }
                  disabled={applying}
                />
                Questions
              </label>
            )}
            {(hasExistingTaxonomyLinks || allProposedItems.length > 0) && (
              <label className="aa-checkbox">
                <input
                  type="checkbox"
                  checked={loadOptions.taxonomy}
                  onChange={(e) =>
                    setLoadOptions((o) => ({ ...o, taxonomy: e.target.checked }))
                  }
                  disabled={applying}
                />
                Taxonomy
              </label>
            )}
            {allProposedItems.length > 0 && canCreateTaxonomy && (
              <label className="aa-checkbox">
                <input
                  type="checkbox"
                  checked={loadOptions.createTaxonomy}
                  onChange={(e) =>
                    setLoadOptions((o) => ({ ...o, createTaxonomy: e.target.checked }))
                  }
                  disabled={applying}
                />
                Create new taxonomy
              </label>
            )}
            {showFullInsert && draft.title && (
              <label className="aa-checkbox">
                <input
                  type="checkbox"
                  checked={loadOptions.useTitle}
                  onChange={(e) =>
                    setLoadOptions((o) => ({ ...o, useTitle: e.target.checked }))
                  }
                  disabled={applying}
                />
                Use suggested title
              </label>
            )}
          </div>
          <div className="aa-load__actions">
            <button
              type="button"
              className="aa-btn aa-btn--primary"
              onClick={handleLoad}
              disabled={applying || isLoading || !canLoad}
            >
              {applying ? 'Loading…' : 'Load'}
            </button>
            <button
              type="button"
              className="aa-icon-btn"
              onClick={handleRegenerate}
              disabled={applying || isLoading}
              aria-label="Regenerate"
              title="Regenerate"
            >
              ⟲
            </button>
            <button
              type="button"
              className="aa-btn aa-btn--ghost aa-btn--sm"
              onClick={handleDiscard}
              disabled={applying}
            >
              Discard
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!capabilitiesLoaded || !canUseAssistant) {
    return null;
  }

  const inCompose =
    !showHistory && (!activePrompt || activePrompt.status === 'error');
  const inResult =
    !showHistory && activePrompt && activePrompt.status !== 'error';

  return (
    <>
      {open && (
        <div className="aa-panel" role="dialog" aria-label="AI prompt">
          <div className="aa-panel__body">
            {showHistory ? (
              <div className="aa-prompt aa-prompt--active">
                <div className="aa-prompt__header">
                  <span className="aa-prompt__label">Previous prompts</span>
                  <div className="aa-prompt__header-actions">
                    <AssistantInfoBubble>
                      <p>Generate content from your prompt, then choose what to load:</p>
                      <ul>
                        <li>Assignment title</li>
                        <li>Directions</li>
                        <li>Questions</li>
                        <li>Taxonomy (including new pathways when permitted)</li>
                      </ul>
                      <p>Load only replaces those fields — points, hours, rubrics, and other setup are unchanged.</p>
                    </AssistantInfoBubble>
                    <button
                      type="button"
                      className="aa-icon-btn"
                      onClick={() => setShowHistory(false)}
                      aria-label="Back to current prompt"
                      title="Back"
                    >
                      ←
                    </button>
                    <button
                      type="button"
                      className="aa-icon-btn aa-icon-btn--close"
                      onClick={() => setOpen(false)}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <div className="aa-history">
                  <p className="aa-history__intro">
                    This session only — cleared when you refresh or leave the page.
                  </p>
                  {promptHistory.map((entry) => (
                    <div key={entry.id} className="aa-history__item">
                      <div className="aa-history__head">
                        <span className="aa-prompt__label">Prompt #{entry.number}</span>
                        <span className="aa-history__action">{entry.action}</span>
                      </div>
                      <p className="aa-history__text">{entry.text}</p>
                      <p className="aa-history__meta">
                        {SCOPE_LABELS[entry.scope] || entry.scope}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="aa-prompt aa-prompt--active">
                <div className="aa-prompt__header">
                  <span className="aa-prompt__label">Prompt #{displayPromptNumber}</span>
                  <div className="aa-prompt__header-actions">
                    <AssistantInfoBubble>
                      <p>Generate content from your prompt, then choose what to load:</p>
                      <ul>
                        <li>Assignment title</li>
                        <li>Directions</li>
                        <li>Questions</li>
                        <li>Taxonomy (including new pathways when permitted)</li>
                      </ul>
                      <p>Load only replaces those fields — points, hours, rubrics, and other setup are unchanged.</p>
                    </AssistantInfoBubble>
                    {promptHistory.length > 0 && (
                      <button
                        type="button"
                        className="aa-icon-btn"
                        onClick={() => setShowHistory(true)}
                        aria-label={`Previous prompts (${promptHistory.length})`}
                        title={`Previous prompts (${promptHistory.length})`}
                      >
                        <span className="aa-icon-btn__glyph" aria-hidden>
                          ⟲
                        </span>
                        <span className="aa-icon-btn__badge">{promptHistory.length}</span>
                      </button>
                    )}
                    {activePrompt?.status === 'loading' && (
                      <span className="aa-prompt__status aa-prompt__status--loading">
                        Generating…
                      </span>
                    )}
                    <button
                      type="button"
                      className="aa-icon-btn aa-icon-btn--close"
                      onClick={() => setOpen(false)}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                </div>

                {inCompose && (
                  <>
                    <textarea
                      id="aa-prompt-input"
                      className="aa-prompt__input"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder="Describe the assignment or content you want generated…"
                      disabled={isLoading}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                    />
                    <p className="aa-fresh-note">{FRESH_REQUEST_NOTE}</p>
                    {activePrompt?.status !== 'error' && (
                      <div className="aa-prompt__generate">
                        <button
                          type="button"
                          className="aa-btn aa-btn--primary aa-btn--block"
                          onClick={handleSend}
                          disabled={isLoading || !input.trim()}
                        >
                          {isLoading ? 'Working…' : 'Generate'}
                        </button>
                      </div>
                    )}
                    {activePrompt?.status === 'error' && (
                      <>
                        <div className="aa-prompt__text">{activePrompt.text}</div>
                        <div className="aa-prompt__error">{activePrompt.error}</div>
                        <div className="aa-prompt__actions">
                          <button
                            type="button"
                            className="aa-btn aa-btn--secondary aa-btn--block"
                            onClick={handleRegenerate}
                            disabled={isLoading}
                          >
                            Try again
                          </button>
                          <button
                            type="button"
                            className="aa-btn aa-btn--ghost aa-btn--block"
                            onClick={handleDiscard}
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}

                {inResult && (
                  <>
                    <div className="aa-prompt__text">{activePrompt.text}</div>
                    {activePrompt.status === 'loading' && (
                      <div className="aa-loading" role="status">
                        <span className="aa-loading__spinner" aria-hidden />
                        {LOADING_STEPS[loadingStep]}
                      </div>
                    )}
                    {activePrompt.status === 'done' && renderActiveResult()}
                  </>
                )}
              </div>
            )}

            {error && !isLoading && (
              <div
                className="aa-toast"
                style={{ background: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c' }}
              >
                {error}
              </div>
            )}

            {toast && <div className="aa-toast">{toast}</div>}
          </div>
        </div>
      )}

      <div className="aa-root">
        <button
          type="button"
          className={`aa-bubble${open ? ' aa-bubble--open' : ''}`}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? 'Close AI' : 'Open AI'}
          title="AI"
        >
          ✦
        </button>
      </div>
    </>
  );
};

export default AssignmentAssistant;
